import React from 'react'
import PropTypes from 'prop-types'
import { css } from 'styled-components'
import { Link as GatsbyLink } from 'gatsby'
import { Helmet } from 'react-helmet'
import { CoreProvider, SessionProvider } from '@rebeldotcom/ui'
import * as components from '@rebeldotcom/components'
import { AccordionPane } from './accordion-pane'

import * as panels from '../panels'

const { Grid, Stack, Box, Text, Icon, theme, Link } = components

const propTypes = {
  children: PropTypes.node.isRequired,
}

const convertToKebabCase = string => {
  return string
    .replace(/([a-z])([A-Z])/g, '$1-$2')
    .replace(/\s+/g, '-')
    .toLowerCase()
}

const convertName = string => {
  return string.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/\s+/g, ' ')
}

const DesignSystemLayout = ({ children }) => {
  const themeObjects = Object.keys(theme).sort()
  const componentObjects = Object.keys(components)
    .filter(c => !c.toLowerCase().includes('theme'))
    .sort()
    .map(c => {
      return {
        slug: c,
        name: c,
      }
    })
  const panelList = Object.keys(panels)
    .sort()
    .map(p => {
      return {
        slug: convertToKebabCase(p),
        name: convertName(p),
      }
    })

  const gridCss = css`
    grid-template-columns: auto 1fr;
    grid-template-rows: auto 1fr;
    grid-template-areas: 'header header' 'sidebar main';
  `

  const renderedThemeList = (
    <Stack as="ul">
      {themeObjects.map(t => (
        <Box key={t} as="li" mb={2}>
          <Link
            ariaLabel={t}
            as={GatsbyLink}
            id={t}
            to={`/demo/design-system/theme/${t}`}
          >
            <Text textTransform="capitalize" variant="milli">
              {t}
            </Text>
          </Link>
        </Box>
      ))}
    </Stack>
  )

  const renderedComponentList = (
    <Stack as="ul">
      {componentObjects.map(c => (
        <Box key={c.slug} as="li" mb={2}>
          <Link
            ariaLabel={c.name}
            as={GatsbyLink}
            id={c.name}
            to={`/demo/design-system/components/${c.slug}`}
          >
            <Text variant="milli">{c.name}</Text>
          </Link>
        </Box>
      ))}
    </Stack>
  )

  const renderedPanelList = (
    <Stack as="ul">
      {panelList.map(p => (
        <Box key={p.slug} as="li" mb={2}>
          <Link
            ariaLabel={p.name}
            as={GatsbyLink}
            id={p.name}
            to={`/demo/design-system/panels/${p.slug}`}
          >
            <Text textTransform="capitalize" variant="milli">
              {p.name}
            </Text>
          </Link>
        </Box>
      ))}
    </Stack>
  )

  return (
    <CoreProvider>
      <>
        <Helmet
          meta={[{ name: 'description', content: 'Rebel Design System' }]}
          title="Rebel Design"
        >
          <html lang="en" />
          <script
            defer
            src="//widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js"
            type="text/javascript"
          />
        </Helmet>
        <SessionProvider>
          <Grid css={gridCss} minHeight="100vh">
            <Box
              alignItems="center"
              as="header"
              bg="greys.1"
              gridArea="header"
              p={3}
            >
              <GatsbyLink to="/demo/design-system">
                <Box>
                  <Icon height="4rem" name="simplerebellogo" width="4rem" />
                  <Text
                    alignItems="center"
                    color="whiteDark"
                    ml={3}
                    variant="teraBold"
                  >
                    Rebel Design
                  </Text>
                </Box>
              </GatsbyLink>
            </Box>
            <Box as="nav">
              <Stack as="ul" bg="greys.8" borderRight={2} gridArea="sidebar">
                <AccordionPane
                  items={[
                    { heading: 'Theme', content: renderedThemeList },
                    {
                      heading: 'Components',
                      content: renderedComponentList,
                    },
                    { heading: 'CMS Panels', content: renderedPanelList },
                  ]}
                />
              </Stack>
            </Box>
            <Box as="main" gridArea="main" mt={4} mx={5}>
              {children}
            </Box>
          </Grid>
        </SessionProvider>
      </>
    </CoreProvider>
  )
}

DesignSystemLayout.propTypes = propTypes

export default DesignSystemLayout
